import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { usePromptDialog } from '../../hooks/usePromptDialog';
import { useEmpathyNormsStore } from '../../stores/empathy-norms.store';
import { Z_GATE } from '../../utils/layers';

/**
 * "How empathy calls work" — the first-call norms (COHORT-COMMUNICATION.md X2,
 * artboard NormsModal). Shown before the first 1:1 call to a user who has not
 * completed the intro, and again only if the norms are revised (server version).
 *
 * Mounted once at the app root, next to CircleAgreementModal, and opened the same
 * way: by the API client when the server refuses a join with 403
 * `empathy_norms_required`. The server's middleware order puts the agreement first
 * when both are owed, so this one only ever comes up after it.
 *
 * Every word on it is served (GET me/empathy-norms): the client renders the copy
 * it is given, so the text a user accepts is the version the server records.
 */
export default function EmpathyNormsModal() {
  const isOpen = useEmpathyNormsStore((s) => s.isOpen);
  const isSubmitting = useEmpathyNormsStore((s) => s.isSubmitting);
  const error = useEmpathyNormsStore((s) => s.error);
  const content = useEmpathyNormsStore((s) => s.content);
  const loadState = useEmpathyNormsStore((s) => s.loadState);
  const load = useEmpathyNormsStore((s) => s.load);
  const accept = useEmpathyNormsStore((s) => s.accept);
  const decline = useEmpathyNormsStore((s) => s.decline);
  const dismiss = useEmpathyNormsStore((s) => s.dismiss);

  const { pathname } = useLocation();
  const openedAtRef = useRef<string | null>(null);

  // Abandon the parked join if the user leaves the page that made it (the same
  // rule as the circle agreement): a Got it after a Back press must not replay a
  // join the user already walked away from.
  useEffect(() => {
    if (!isOpen) {
      openedAtRef.current = null;
      return;
    }
    if (openedAtRef.current === null) {
      openedAtRef.current = pathname;
      return;
    }
    if (openedAtRef.current !== pathname) {
      decline();
    }
  }, [isOpen, pathname, decline]);

  // The dialog contract (hooks/usePromptDialog): focus moves in, Tab stays
  // inside, Escape and Android Back decline (as before: the parked join is
  // not replayed), focus goes back to the opener. It IS the gate. While Got
  // it is saving they hold: the server's answer settles the join (c-065).
  const { dialogRef } = usePromptDialog(isOpen, dismiss, {
    yieldToGate: false,
  });

  const copy = loadState === 'ready' ? content?.copy : undefined;
  const learnMoreTo = content?.learn_more_lesson_id
    ? `/app/lesson/${content.learn_more_lesson_id}`
    : '/app';

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key="empathy-norms-overlay"
          ref={dialogRef}
          tabIndex={-1}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={copy ? 'empathy-norms-title' : undefined}
          aria-label={copy ? undefined : 'How empathy calls work'}
          // Above the mobile bottom nav's floating E button (z-99, dashboard/layout.tsx)
          // and every sheet that can make a gated request (P7-1).
          data-testid="empathy-norms-modal"
          style={{ zIndex: Z_GATE }}
          className="fixed inset-0 flex items-center justify-center bg-(--ee-scrim)/55 p-4 outline-none"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="relative bg-base-100 text-base-content rounded-[22px] px-5 pt-6 pb-5 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto flex flex-col gap-4"
          >
            <button
              type="button"
              onClick={decline}
              disabled={isSubmitting}
              aria-label="Close"
              className="absolute top-3 right-3 p-2 rounded-full text-base-content/60 hover:text-base-content hover:bg-base-200"
            >
              <FiX aria-hidden="true" />
            </button>

            {copy ? (
              <>
                <h2
                  id="empathy-norms-title"
                  className="m-0 pr-8 text-[22px] font-bold leading-tight"
                >
                  {copy.title}
                </h2>
                <ol className="flex flex-col gap-4 m-0 p-0 list-none">
                  {copy.lines.map((line, i) => (
                    <li key={line} className="flex gap-3 items-start">
                      <span
                        aria-hidden="true"
                        className="w-7 h-7 rounded-full bg-primary/15 text-primary text-sm font-bold flex items-center justify-center shrink-0"
                      >
                        {i + 1}
                      </span>
                      <span className="text-[15px] leading-snug pt-0.5">{line}</span>
                    </li>
                  ))}
                </ol>

                {error ? (
                  <p
                    role="alert"
                    className="text-sm text-error bg-error/10 border border-error/30 rounded-xl p-3 m-0"
                  >
                    {error}
                  </p>
                ) : null}

                <button
                  type="button"
                  data-testid="empathy-norms-accept"
                  onClick={() => void accept()}
                  disabled={isSubmitting}
                  className="btn btn-primary w-full min-h-[52px] rounded-xl text-base"
                >
                  {isSubmitting ? 'Saving…' : copy.acknowledge}
                </button>
                <div className="text-center text-sm">
                  {/* Leaving the page declines the parked join (the pathname effect above). */}
                  <Link to={learnMoreTo} className="link link-primary">
                    {copy.learn_more}
                  </Link>
                </div>
              </>
            ) : loadState === 'failed' ? (
              <div className="flex flex-col gap-4 pt-6">
                <p role="alert" className="text-sm m-0">
                  We couldn&rsquo;t load this. Check your connection and try again.
                </p>
                <button
                  type="button"
                  onClick={() => void load()}
                  className="btn btn-primary w-full min-h-[52px] rounded-xl"
                >
                  Try again
                </button>
              </div>
            ) : (
              <div className="flex justify-center py-10">
                <span className="loading loading-spinner loading-lg text-primary" />
              </div>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
